import { useContext, useEffect, useRef, useState } from 'react';
import notify from '../../Utils/notify';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from "@fullcalendar/interaction" // needed for dayClick
import esLocale from '@fullcalendar/core/locales/es';
import axios from '../../axios';
import EditCitaModal from './EditCitaModal';
import NewCitaModal from './NewCitaModal';
import { UserContext } from '../../UserContext';
import { checkPermiso } from '../../Utils/PermissionsFunctions';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

const today = new Date();
const firstDayMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
const lastDayMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

const VerAgenda = () => {
    const {user} = useContext(UserContext);
    const [calendarView,setCalendarView] = useState('dayGridWeek');
    const [calendarApi,setCalendarApi] = useState();
    const [sucursales,setSucursales] = useState([]);
    const [sucursalSeleccionada, setSucursalSeleccionada] = useState();
    const [citasEvents,setCitasEvents] = useState([]);
    const [citas,setCitas] = useState([]);
    const [holidays,setHolidays] = useState([]);
    const [citaSeleccionada,setCitaSeleccionada] = useState(null);
    const [sincronizando, setSincronizando] = useState(false);
    const [cargandoCitas, setCargandoCitas] = useState(false);
    const [editCitaModalOpen, setEditCitaModalOpen] = useState(false);
    const [newCitaModalOpen, setNewCitaModalOpen] = useState(false);
    const [slotSeleccionado, setSlotSeleccionado] = useState(null);
    const [festivosCargados, setFestivosCargados] = useState(false);
    const [validRange, setValidRange] = useState(undefined); // Rango válido para el calendario
    const [mesActivo, setMesActivo] = useState({ mes: today.toLocaleDateString('es-ES', { month: 'long' }), año: today.getFullYear() });
    const [permisos,setPermisos] = useState({
        appointments_calendar_read:null,
        appointments_update:null,
        appointments_clean:null
    })
    const calendar = useRef();

    useEffect(()=>{
        if(user.id){
            setPermisos((prevPremisos) => ({
                ...prevPremisos,
                appointments_calendar_read:checkPermiso("appointments_calendar_read",user.permissions),
                appointments_update:checkPermiso("appointments_update",user.permissions),
                appointments_clean:checkPermiso("appointments_clean",user.permissions)
            }));
            user.user_roles && user.user_roles.includes('admin') ? setValidRange(undefined) : setValidRange({end: new Date(new Date().setMonth(new Date().getMonth() + 2))});
            getSucursales();
       }
      
    },[user,sucursalSeleccionada,citaSeleccionada]);

    useEffect(() => {
        if (calendar.current) {
            setCalendarApi(calendar.current.getApi());
        }
    }, []);

    // Cargar festivos del año una sola vez al seleccionar sucursal
    useEffect(() => {
        if (!sucursalSeleccionada) {
            setFestivosCargados(false);
            setHolidays([]);
            return;
        }
        
        const añoActual = new Date().getFullYear();
        
        axios.post('api/holidays/en-rango', {
            fecha_inicio: `${añoActual}-01-01`,
            fecha_fin: `${añoActual}-12-31`,
            sucursal_id: parseInt(sucursalSeleccionada)
        }).then((respHolidays) => {
            const festivosInfo = respHolidays.data.festivos || [];
            setHolidays(festivosInfo);
            setFestivosCargados(true);
        }).catch(e => {
            console.error('Error cargando festivos:', e);
            setHolidays([]);
            setFestivosCargados(true); // Marcar como cargado aunque haya fallado
        });
    }, [sucursalSeleccionada]);

    useEffect(()=>{
        if(sucursalSeleccionada && citaSeleccionada===null && festivosCargados){
            _getCitasOfTheMonth(firstDayMonth,lastDayMonth);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[sucursalSeleccionada,citaSeleccionada,festivosCargados])

    const getSucursales = () =>{
        axios.get('/api/sucursals').then((resp)=>{
            setSucursales(resp.data);
            if (!sucursalSeleccionada) {
                const sucursal = localStorage.getItem('sucursal_selected');
                if (sucursal) setSucursalSeleccionada(sucursal);
            }
        }).catch(e=>console.log("Error: ",e)); 
    }

    const _getCitasOfTheMonth = (fDay,lDay) => {
        if(permisos.appointments_calendar_read && !cargandoCitas){
            setCargandoCitas(true);

            // Usar el nuevo endpoint de rango optimizado
            axios.post('api/agenda-slots-range', {
                sucursal_id: sucursalSeleccionada,
                fecha_inicio: fDay,
                fecha_fin: lDay
            }).then((resp) => {
                const todosLosSlots = resp.data;
                
                let eventParse = todosLosSlots.map((item, index) => {
                    const isOccupied = item._type === 'occupied';
                    const excedido = Number(item.excedido) || 0;
                    
                    // Determinar color según estado
                    let color;
                    if (!isOccupied) {
                        color = '#e0f2fe'; // Azul muy claro para slots vacíos
                    } else if (item.status === 'cancelada') {
                        color = '#ef4444'; // Rojo para canceladas
                    } else if (excedido > 0) {
                        color = '#fb923c'; // Naranja para excedidas
                    } else {
                        color = '#4ade80'; // Verde para a tiempo
                    }
                    
                    return {
                        id: item.id || `slot-${item._slot_key}`,
                        title: item.hora,
                        start: item.fecha,
                        color: color,
                        extendedProps: {
                            tipo: isOccupied ? 'cita' : 'slot_vacio',
                            paciente: item.nombre_paciente || '',
                            excedido: excedido,
                            products: item.products || [],
                            _type: item._type,
                            status: item.status || null,
                            traslape: excedido > 0 // Indica si hay traslape
                        }
                    }
                });
                
                // Usar festivos ya cargados en memoria
                const holidayEvents = holidays.map((holiday, idx) => ({
                    id: `holiday-${holiday.id || idx}`,
                    title: holiday.nombre || 'Festivo',
                    start: holiday.fecha,
                    allDay: true,
                    display: 'background',
                    backgroundColor: '#fef3c7',
                    borderColor: '#fbbf24',
                    extendedProps: {
                        tipo: 'festivo',
                        descripcion: holiday.descripcion || '',
                        recurrente: holiday.recurrente || false
                    }
                }));
                
                setCitasEvents([...eventParse, ...holidayEvents]);
                setCitas(todosLosSlots.filter(s => s._type === 'occupied'));
                setCargandoCitas(false);
            }).catch(e => {
                console.log("Error cargando slots:", e);
                setCargandoCitas(false);
            });
        }
    }

    const sincronizarCitas = () => {
        if (!sucursalSeleccionada || !calendarApi || cargandoCitas) return;
        
        setSincronizando(true);
        setCargandoCitas(true);
        const currentView = calendarApi.view;
        const firstDay = new Date(currentView.currentStart).toISOString().split('T')[0];
        const lastDay = new Date(currentView.currentEnd).toISOString().split('T')[0];
        
        // Usar el nuevo endpoint de rango optimizado
        axios.post('api/agenda-slots-range', {
            sucursal_id: sucursalSeleccionada,
            fecha_inicio: firstDay,
            fecha_fin: lastDay
        }).then((resp) => {
            const todosLosSlots = resp.data;
    
            let eventParse = todosLosSlots.map((item, index) => {
                const isOccupied = item._type === 'occupied';
                const excedido = Number(item.excedido) || 0;
                
                // Determinar color según estado
                let color;
                if (!isOccupied) {
                    color = '#e0f2fe'; // Azul muy claro para slots vacíos
                } else if (item.status === 'cancelada') {
                    color = '#ef4444'; // Rojo para canceladas
                } else if (excedido > 0) {
                    color = '#fb923c'; // Naranja para excedidas
                } else {
                    color = '#4ade80'; // Verde para a tiempo
                }
                
                return {
                    id: item.id || `slot-${item._slot_key}`,
                    title: item.hora,
                    start: item.fecha,
                    color: color,
                    extendedProps: {
                        tipo: isOccupied ? 'cita' : 'slot_vacio',
                        paciente: item.nombre_paciente || '',
                        excedido: excedido,
                        products: item.products || [],
                        _type: item._type,
                        status: item.status || null,
                        traslape: excedido > 0
                    }
                }
            });
            
            // Usar festivos ya cargados en memoria
            const holidayEvents = holidays.map((holiday, idx) => ({
                id: `holiday-${holiday.id || idx}`,
                title: holiday.nombre || 'Festivo',
                start: holiday.fecha,
                display: 'background',
                backgroundColor: '#fef3c7',
                allDay: true,
                borderColor: '#fbbf24',
                extendedProps: {
                    tipo: 'festivo',
                    isHoliday: true
                }
            }));

            setCitasEvents([...eventParse, ...holidayEvents]);
            setCitas(todosLosSlots.filter(s => s._type === 'occupied'));
            setSincronizando(false);
            setCargandoCitas(false);
        }).catch(e => {
            console.log("Error al sincronizar: ", e);
            setSincronizando(false);
            setCargandoCitas(false);
        });
    };


    /** Inicio --- Funciones del calendario **/
    const changeCalendarView = (view) =>{
        setCalendarView(view)
        calendarApi.changeView(view)
    }

    const renderEventContent = (eventInfo) => {
        // Si es un evento de festivo, no renderizar contenido personalizado
        if (eventInfo.event.extendedProps.tipo === 'festivo') {
            return null;
        }

        const isOccupied = eventInfo.event.extendedProps._type === 'occupied';
        const excedido = eventInfo.event.extendedProps.excedido || 0;
        const hasTraslape = eventInfo.event.extendedProps.traslape;

        return (
            <div className={`p-1 relative ${!isOccupied ? 'cursor-pointer hover:opacity-80' : 'cursor-pointer'}`}>
                {/* Indicador visual de traslape */}
                {hasTraslape && (
                    <div className="absolute top-0 right-0 bg-red-500 text-white text-[10px] px-1 rounded-bl z-10">
                        ⚠️ Traslape
                    </div>
                )}
                
                <div className={`font-bold text-xs relative flex items-center gap-1 ${!isOccupied ? 'text-gray-900' : ''}`}>
                    <i>{eventInfo.event.title}</i>
                    {excedido > 0 && (
                        <span className="text-[10px] text-red-700 font-semibold">
                            +{excedido}min
                        </span>
                    )}
                </div>
                
                {isOccupied ? (
                    <div className="text-xs">
                        <p className='m-0'><b>Paciente:</b> {eventInfo.event.extendedProps.paciente}</p>
                        {eventInfo.event.extendedProps.products?.length > 0 && (
                            <div className='m-0'>
                                <b>Estudios:</b>
                                {eventInfo.event.extendedProps.products.map((p, index) => (
                                    <p className='m-0 ml-2' key={index}>
                                        • {p.nombre_producto} ({p.duracion_aproximada || 0} min)
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <p className='m-0 text-xs text-gray-900 font-medium'>
                        📅 Click para agendar
                    </p>
                )}
            </div>
        )
    };

    const handleChangedMonth = (event) =>{
        // Evitar carga si ya está cargando
        if (cargandoCitas) {
            return;
        }
        
        let firstDayMonth = new Date(event.startStr).toISOString().split('T')[0];
        let lastDayMonth = new Date(event.endStr).toISOString().split('T')[0];
        
        // Actualizar el mes activo que se está mostrando
        const fechaVisible = new Date(event.start);
        const nuevoMes = fechaVisible.toLocaleDateString('es-ES', { month: 'long' });
        const nuevoAño = fechaVisible.getFullYear();
        
        // Solo actualizar si el mes realmente cambió
        if (nuevoMes !== mesActivo.mes || nuevoAño !== mesActivo.año) {
            setMesActivo({
                mes: nuevoMes,
                año: nuevoAño
            });
        }
        
        _getCitasOfTheMonth(firstDayMonth,lastDayMonth);     
    }

    const openEditModal = async (citaId) => {
        // Obtener versión fresca de la cita antes de abrir el modal
        try {
            const resp = await axios.get(`api/appointments/${citaId}`);
            const citaFresca = resp.data;
            
            // Verificar si la cita ya está ocupada (tiene paciente y productos)
            if (citaFresca.nombre_paciente && citaFresca.products?.length > 0) {
                const source = citaFresca.source || 'Desconocido';
                const confirmMsg = `⚠️ ADVERTENCIA: Esta cita ya está ocupada\n\n` +
                    `Paciente: ${citaFresca.nombre_paciente}\n` +
                    `Teléfono: ${citaFresca.telefono_paciente || 'N/A'}\n` +
                    `Origen: ${source}\n` +
                    `Estudios: ${citaFresca.products.length}\n\n` +
                    `Editarla podría causar conflictos con otra reserva.\n` +
                    `¿Deseas continuar de todos modos?`;
                
                if (!window.confirm(confirmMsg)) {
                    notify.info('Edición cancelada');
                    return;
                }
            }
            
            setCitaSeleccionada(citaId);
            setEditCitaModalOpen(true);
        } catch (e) {
            console.log('Error al cargar la cita:', e);
            notify.error('Error al cargar la cita. Intenta refrescar la lista.');
        }
    };

    const _eventDetail = ({event}) =>{
        if (!permisos.appointments_update) {
            alert("No tienes permiso de editar citas");
            return;
        }

        const eventType = event.extendedProps._type;
        const isFestivo = event.extendedProps.tipo === 'festivo';
        
        // No permitir interacción con días festivos
        if (isFestivo) {
            return;
        }
        
        // Si es un slot disponible, abrir modal para crear cita
        if (eventType === 'empty') {
            const fecha = event.startStr; // Formato YYYY-MM-DD
            const hora = event.title; // La hora está en el título
            
            setSlotSeleccionado({
                fecha: fecha,
                hora: hora,
                sucursal_id: sucursalSeleccionada
            });
            setNewCitaModalOpen(true);
        } 
        // Si es un slot ocupado, abrir modal para editar
        else if (eventType === 'occupied') {
            openEditModal(event.id);
        }
    }

    /** FIN -- Funciones del calendario **/

    return (
        <div>
            <div className="container-admin-menu">
                <Breadcrumb hasButton={false} hasFilters={false} />
            </div>
            <div className="container-admin-container">
                <div className="container-admin bg-white rounded-lg shadow-md p-4">
                    {/* Modales */}
                    <EditCitaModal
                        open={editCitaModalOpen}
                        onClose={() => {
                            setEditCitaModalOpen(false);
                            setCitaSeleccionada(null);
                            // Recargar las citas después de editar
                            if (calendarApi) {
                                const currentView = calendarApi.view;
                                const firstDay = new Date(currentView.currentStart).toISOString().split('T')[0];
                                const lastDay = new Date(currentView.currentEnd).toISOString().split('T')[0];
                                _getCitasOfTheMonth(firstDay, lastDay);
                            }
                        }}
                        citaId={citaSeleccionada}
                        citas={citas}
                        setCitas={setCitas}
                        appointments_clean={permisos.appointments_clean}
                    />
                    
                    <NewCitaModal
                        open={newCitaModalOpen}
                        onClose={() => {
                            setNewCitaModalOpen(false);
                            setSlotSeleccionado(null);
                        }}
                        sucursalId={slotSeleccionado?.sucursal_id}
                        fecha={slotSeleccionado?.fecha}
                        hora={slotSeleccionado?.hora}
                        slotDuracionMinutos={30}
                        onCreated={() => {
                            // Recargar las citas después de crear una nueva
                            if (calendarApi) {
                                const currentView = calendarApi.view;
                                const firstDay = new Date(currentView.currentStart).toISOString().split('T')[0];
                                const lastDay = new Date(currentView.currentEnd).toISOString().split('T')[0];
                                _getCitasOfTheMonth(firstDay, lastDay);
                            }
                        }}
                    />

                    {/* Header con filtros */}
                    <div className="flex flex-col gap-4 info-badge rounded-xl p-4 shadow-sm mb-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <img className="w-20" src="/images/calendar.png" alt="Calendario" />
                                <h4 className="text-base font-bold text-white">Ver Agenda</h4>
                            </div>
                            
                            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
                                <div className="w-full md:w-64">
                                    <select
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 browser-default text-gray-600"
                                        value={sucursalSeleccionada || ''}
                                        onChange={(e) => setSucursalSeleccionada(e.target.value)}
                                    >
                                        <option value="" disabled>Seleccione la sucursal</option>
                                        {sucursales.map((sucursal) => (
                                            <option value={sucursal.id} key={sucursal.id}>
                                                {sucursal.nombre_sucursal}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <button
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={sincronizarCitas}
                                    disabled={sincronizando || !sucursalSeleccionada}
                                    title={!sucursalSeleccionada ? "Seleccione una sucursal primero" : "Sincronizar citas"}
                                >
                                    <ArrowPathIcon className={`h-5 w-5 ${sincronizando ? 'animate-spin' : ''}`} />
                                    {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => changeCalendarView('dayGridMonth')}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors border border-blue-200"
                            >
                                Ver Mes
                            </button>
                            <button
                                type="button"
                                onClick={() => changeCalendarView('dayGridWeek')}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors border border-blue-200"
                            >
                                Ver Semana
                            </button>
                            <button
                                type="button"
                                onClick={() => changeCalendarView('dayGridDay')}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-md text-sm font-medium hover:bg-blue-50 transition-colors border border-blue-200"
                            >
                                Ver Día
                            </button>
                        </div>
                    </div>

                    {/* Indicadores de fecha */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
                                    {today.getDate()}
                                </div>
                                <div>
                                    <p className="text-xs text-blue-600 font-medium uppercase">Día Actual</p>
                                    <p className="text-sm font-bold text-blue-900">
                                        {today.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-purple-50 border-l-4 border-purple-500 rounded-lg p-4 shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="bg-purple-500 text-white rounded-full w-12 h-12 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-xs text-purple-600 font-medium uppercase">Mostrando</p>
                                    <p className="text-sm font-bold text-purple-900 capitalize">
                                        {mesActivo.mes} {mesActivo.año}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Leyenda de colores */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">Leyenda de colores</h5>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{backgroundColor: '#4ade80'}}></div>
                                <span className="text-xs text-gray-600">✓ A tiempo</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{backgroundColor: '#fb923c'}}></div>
                                <span className="text-xs text-gray-600">⚠️ Excedida</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{backgroundColor: '#ef4444'}}></div>
                                <span className="text-xs text-gray-600">✖ Cancelada</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{backgroundColor: '#e0f2fe'}}></div>
                                <span className="text-xs text-gray-600">○ Disponible</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{backgroundColor: '#fef3c7'}}></div>
                                <span className="text-xs text-gray-600">🎉 Festivo</span>
                            </div>
                        </div>
                    </div>

                    {/* Calendario */}
                    <div className="bg-white rounded-lg p-4 calendar-agenda relative">
                        {cargandoCitas && (
                            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-20 rounded-lg">
                                <div className="flex flex-col items-center gap-3">
                                    <ArrowPathIcon className="h-10 w-10 text-blue-600 animate-spin" />
                                    <p className="text-sm font-medium text-gray-600">Cargando citas...</p>
                                </div>
                            </div>
                        )}
                        <FullCalendar
                            ref={calendar}
                            plugins={[dayGridPlugin, interactionPlugin]}
                            initialView={calendarView}
                            locale={esLocale}
                            selectable={true}
                            selectHelper={true}
                            editable={true}
                            events={citasEvents}
                            eventContent={renderEventContent}
                            datesSet={handleChangedMonth}
                            eventClick={(e) => _eventDetail(e)}
                            showNonCurrentDates={false}
                            validRange={validRange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerAgenda;